import { useEffect } from 'react'
import { setGuidedReturn } from '../../redux/slices/checkout'
import useAppDispatch from '../useAppDispatch'

/**
 * Ends any guided walk, because the shopper is no longer inside one.
 *
 * `checkoutSlice.guidedReturnPath` is set by the summary CTA before it pushes
 * the first picker, and read by the picker screens' shared `confirm()`. This
 * is what guarantees it is gone everywhere else.
 *
 * **Mounted on the two screens a walk can end on** — `templates/Summary.tsx`
 * and `templates/Cart.tsx`. Arriving at either means the walk is over, however
 * the shopper got there: the walk's own final hop, the picker's Back control,
 * the webview's back gesture, or an authored link.
 *
 * ⚠️ **The cart mount is the load-bearing one, and it is easy to think
 * redundant.** COM-1045's *Cambiar* row and COM-1050's recovery redirect both
 * push into a picker **from the cart**, and both rely on `guidedReturnPath`
 * being absent so that `confirm()` returns the shopper to the cart. Clear only
 * on the summary and that holds for every path *through* the summary — but a
 * shopper who leaves a picker straight to the cart keeps a stale return path,
 * and their next *Cambiar* confirm routes them into the other picker or onto a
 * summary they never asked for. Clearing here makes `confirm()`'s "absence
 * means they came from the cart" literally true rather than contingent on
 * which screens the shopper happened to pass through.
 *
 * Deliberately **not** called from the pickers' own `back`, which writes
 * nothing by design: a shopper mid-walk who backs onto an *earlier picker* is
 * still in the walk, and clearing there would drop them out of it. Ending the
 * walk is about arriving somewhere that is not a picker, not about leaving one.
 *
 * Mount-only. Re-running it on every render would fight the summary CTA, which
 * writes the field and then navigates away within the same interaction.
 */
export const useEndGuidedWalk = (): void => {
  const dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(setGuidedReturn(undefined))
  }, [dispatch])
}

export default useEndGuidedWalk
